import Button from "@/components/Button"
import ButtonGroup from "@/components/ButtonGroup"
import { SearchInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import {
  ClinicalProtocolTypeFilter,
  StatusFilter,
  usePatientListPageDataQuery,
} from "@/data/queries"
import { ClinicalProtocolType } from "@/model/ClinicalProtocol"
import { Organization } from "@/model/Organization"
import { Patient } from "@/model/Patient"
import { Prescription } from "@/model/Prescription"
import OrganizationDropdown from "@/pages/patient-list/OrganizationDropdown"
import { buildPath, ROUTES } from "@/routes"
import cn from "@/utils/cn"
import {
  formatDate,
  formatDateTime,
  formatDaysBetween,
} from "@/utils/date-utils"
import { useSearchParams } from "@/utils/hooks/useSearchParams"
import { Instant } from "@js-joda/core"
import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useState,
} from "react"
import AddPatientDialog from "./AddPatientDialog"
import DataCell from "./DataCell"
import HeaderCell from "./HeaderCell"
import OrganizationCapsule from "./OrganizationCapsule"
import ViewLink from "./ViewLink"

export type PatientListPageSearchParams = {
  organizationId: string | "all"
  protocol: ClinicalProtocolType | "all"
  filter: StatusFilter
  search?: string
}

const PatientListPage = () => {
  const [searchParams, setSearchParams] =
    useSearchParams<PatientListPageSearchParams>()
  const {
    organizationId = "all",
    protocol = "all",
    filter = "all",
    search,
  } = searchParams

  const {
    isPending: isLoadingPatients,
    isError: isErrorLoadingPatients,
    data: population,
  } = usePatientListPageDataQuery({
    organizationId,
    protocol,
    filter,
    search,
  })

  const [isAddPatientDialogOpen, setIsAddPatientDialogOpen] = useState(false)

  const openAddPatientDialog = useCallback(
    () => setIsAddPatientDialogOpen(true),
    []
  )
  const closeAddPatientDialog = useCallback(
    () => setIsAddPatientDialogOpen(false),
    []
  )

  const {
    organizations = [],
    patients = [],
    prescriptions = [],
    filterSummary,
  } = population ?? {}

  const patientFor = (prescription: Prescription) =>
    patients.find((patient) => patient.id === prescription.patientId)!

  const organizationFor = (prescription: Prescription) =>
    organizations.find(
      (organization) => organization.id === prescription.organizationId
    )!

  useEffect(() => {
    if (!protocol) {
      setSearchParams((prev) => ({ ...prev, protocol: "all" }))
    }
    if (!filter) {
      setSearchParams((prev) => ({ ...prev, filter: "all" }))
    }
    if (!organizationId) {
      setSearchParams((prev) => ({ ...prev, organizationId: "all" }))
    }
  }, [protocol, organizationId, filter, setSearchParams])

  const onClickProtocol = (value: ClinicalProtocolType | "all") => {
    setSearchParams((prev) => ({
      ...prev,
      protocol: value === searchParams.protocol ? "all" : value,
      filter:
        value === searchParams.protocol || value === "all"
          ? "all"
          : prev.filter,
    }))
  }

  const onChangeSearch = (value: string) =>
    setSearchParams(() => ({
      organizationId: "all",
      filter: "all",
      protocol: "all",
      search: value,
    }))

  const onChangeOrganization = (organization: Organization | null) =>
    setSearchParams((prev) => ({
      ...prev,
      organizationId: organization?.id ?? "all",
    }))

  const onClickFilter = (value: StatusFilter) =>
    setSearchParams((prev) => ({ ...prev, filter: value }))

  return (
    <Page>
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-extrabold">Patients Overview</h1>
        <div className="flex items-center gap-3">
          <div>
            <SearchInput
              placeholder="Search"
              className="w-[340px]"
              value={search ?? ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeSearch(e.target.value)
              }
            />
          </div>
          <Button
            variant="primary"
            className="text-md flex gap-1 rounded-xl px-4 py-3 text-[14px] tracking-wider"
            onClick={openAddPatientDialog}
          >
            <span className="material-icons text-icon-lg">add</span>
            Patient
          </Button>
        </div>
      </div>

      <div className="mb-8 mt-6 flex items-center gap-2">
        <div className="flex items-center gap-3">
          <OrganizationDropdown
            organizations={organizations}
            value={
              organizations.find(
                (organization) => organization.id == organizationId
              ) ?? null
            }
            onChange={onChangeOrganization}
          />
          {!isLoadingPatients && (
            <>
              <div className="h-20 w-[0.75px] bg-primary/20"></div>
              <div className="flex flex-col gap-3">
                <ButtonGroup
                  variant="rounded"
                  aria-label="Patient population"
                  values={
                    filterSummary
                      ? serviceOptions(filterSummary.protocolType)
                      : []
                  }
                  value={protocol}
                  onClick={onClickProtocol}
                />
                <div className="h-[0.75px] bg-primary/20" />
                <ButtonGroup
                  aria-label="patient filters"
                  values={
                    filterSummary ? filterOptions(filterSummary.status) : []
                  }
                  variant="rounded"
                  value={filter}
                  onClick={onClickFilter}
                  customCheckedStyles="data-checked:bg-[#e6c0dd] data-checked:text-primary/90"
                />
              </div>
            </>
          )}
        </div>
      </div>
      {!isLoadingPatients && (
        <div className="-mx-8 overflow-x-auto">
          <div className="grid min-w-[1200px] grid-cols-[0.4fr_0.4fr_max-content_max-content_0.7fr_0.8fr_0.6fr_auto] gap-x-3 px-8">
            <div className="col-span-8 grid grid-cols-subgrid items-end border-b border-gray-200">
              <HeaderCell className="ms-3" title="Organization">
                Organization
              </HeaderCell>
              <HeaderCell title="Service (Diabetes / Hypertension)">
                Service
              </HeaderCell>
              <HeaderCell title="Medical Record Number">MRN</HeaderCell>
              <HeaderCell title="Patient name">Patient</HeaderCell>
              <HeaderCell title="First measurement">
                First measurement
              </HeaderCell>
              <HeaderCell title="Time since last measurement">
                Last measurement
              </HeaderCell>
              <HeaderCell title="Estimated Delivery Date">EDD</HeaderCell>
              <HeaderCell />
            </div>
            {prescriptions.map((prescription, index) => {
              const organization = organizationFor(prescription)
              return (
                <PatientRow
                  key={`${prescription.id}-${index}`}
                  patient={patientFor(prescription)}
                  prescription={prescription}
                  organizationShortName={organization.shortName}
                  organizationLongName={organization.longName}
                  organizationIndex={organizations.indexOf(organization)}
                  className={cn(
                    "border-b border-gray-200",
                    index % 2 !== 0 ? "bg-white" : "bg-gray-50"
                  )}
                />
              )
            })}
          </div>
        </div>
      )}
      {isLoadingPatients && (
        <div className="mt-6 flex items-center justify-center">
          <Spinner className="me-2" />
          Loading patients...
        </div>
      )}
      {isErrorLoadingPatients ? (
        <div className="p-4 text-center italic text-red-600">
          Failed to load patients. Please try again later.
        </div>
      ) : !isLoadingPatients && patients.length === 0 ? (
        <div className="p-4 text-center italic text-primary/60">
          {filter === "all"
            ? "No patients found."
            : "No patients found matching the selected filters."}
        </div>
      ) : null}
      <AddPatientDialog
        organizations={organizations}
        isOpen={isAddPatientDialogOpen}
        onClose={closeAddPatientDialog}
      />
    </Page>
  )
}

type PatientRowProps = {
  patient: Patient
  prescription: Prescription
  organizationShortName: string
  organizationLongName: string
  organizationIndex: number
} & Omit<ComponentPropsWithoutRef<"div">, "children">

const PatientRow = ({
  patient,
  prescription,
  organizationShortName,
  organizationLongName,
  organizationIndex,
  className,
  ...otherProps
}: PatientRowProps) => (
  <div
    className={cn("col-span-8 grid grid-cols-subgrid", className)}
    {...otherProps}
  >
    <DataCell className="ms-3">
      <OrganizationCapsule
        title={organizationShortName}
        tooltip={organizationLongName}
        index={organizationIndex + 1}
        className="w-full"
      />
    </DataCell>
    <DataCell
      title={prescription.type === "diabetes" ? "Diabetes" : "Hypertension"}
    >
      <span className="truncate">
        {prescription.type === "diabetes" ? "DM" : "HTN"}
      </span>
    </DataCell>
    <DataCell>{patient.medicalRecordNumber ?? "-"}</DataCell>
    <DataCell title={`${patient.firstName} ${patient.lastName}`}>
      {patient.lastName}, {patient.firstName}
    </DataCell>
    <DataCell
      title={
        prescription.firstReadingTime
          ? formatDateTime(prescription.firstReadingTime, "MMMM d, yyyy h:mm a")
          : undefined
      }
    >
      <span className="truncate">
        {prescription.firstReadingTime
          ? formatDateTime(prescription.firstReadingTime, "MM/dd/yy")
          : "-"}
      </span>
    </DataCell>
    <DataCell
      title={
        prescription.lastReadingTime
          ? formatDateTime(prescription.lastReadingTime, "MMMM d, yyyy h:mm a")
          : undefined
      }
    >
      <span className="truncate">
        {prescription.lastReadingTime
          ? formatDaysBetween(prescription.lastReadingTime, Instant.now())
          : "-"}
      </span>
    </DataCell>
    <DataCell
      title={
        prescription.estimatedDateOfDelivery
          ? formatDate(prescription.estimatedDateOfDelivery, "MMMM d, yyyy")
          : undefined
      }
    >
      <span className="truncate">
        {prescription.estimatedDateOfDelivery
          ? formatDate(prescription.estimatedDateOfDelivery, "MM/dd/yy")
          : "-"}
      </span>
    </DataCell>
    <DataCell className="me-3">
      <ViewLink
        to={buildPath(ROUTES.POPULATION.PATIENT_DETAIL, {
          bloomlifeId: patient.bloomlifeId,
        })}
      />
    </DataCell>
  </div>
)

type ClinicalProtocolTypeOption = {
  value: ClinicalProtocolTypeFilter
  title: string
}

type StatusOption = {
  value: StatusFilter
  title: string
}

const serviceOptions = (
  summary: Record<ClinicalProtocolTypeFilter, number>
): ClinicalProtocolTypeOption[] => [
  {
    value: "all",
    title: `All (${summary.all})`,
  },
  {
    value: "diabetes",
    title: `Diabetes (${summary.diabetes})`,
  },
  {
    value: "hypertension",
    title: `Hypertension (${summary.hypertension})`,
  },
]

const filterOptions = (
  summary: Record<StatusFilter, number>
): StatusOption[] => [
  {
    value: "monitoring",
    title: `Monitoring (${summary.monitoring})`,
  },
  {
    value: "noReadingInLast24Hours",
    title: `No readings < 24 hours (${summary.noReadingInLast24Hours})`,
  },
  {
    value: "noFirstReading",
    title: `No first reading (${summary.noFirstReading})`,
  },
  {
    value: "noDevices",
    title: `No device (${summary.noDevices})`,
  },
]

export default PatientListPage
